import { DataStorageEnums } from 'dashboard-services/api/dataStorage';
import { CurveKey, GetCurveBody } from 'dashboard-services/api/dataTransform';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import { ActionService, CommonService, ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { globalActions, statusBoxActions } from 'primary-components';

import { customInsightSelectors } from 'selectors/modules/insight/custom';
import SqlSourceService from 'services/SqlSourceService'

import { basicCustomInsightActions } from '../../';
import additionalColumnsCustomInsightActions from '../additionalColumns';
import basicCustomInsightConsts from '../basicConsts.js';
import chartCustomInsightActions from '../chart.js';
import { buildCurveSymbol } from '../curveSymbols';
import tableCustomInsightActions from '../table.js';

import dayjs from 'dayjs';

import { parse, unparse } from 'papaparse';

const { getInputParamsConfig } = customInsightSelectors
const { BASIC_INPUT_PARAM_TYPES } = basicCustomInsightConsts
export default (() => {
  const { TILE_TYPES, INPUT_PARAMS_DATE_RANGE_TYPES, INPUT_PARAMS_PRESET_TYPES, INPUT_PARAMS_SINGLE_DATE_RANGE_TYPES } = basicCustomInsightConsts
  const CURVE_DATA_ERROR_MESSAGE = "Could not load the curve data."
  const latestSqlRequestByInputParam = {}
  const latestDatePresetRequestByInputId = new Map()
  const startSqlRequest = inputParamId => {
    const requestId = (latestSqlRequestByInputParam[inputParamId] || 0) + 1
    latestSqlRequestByInputParam[inputParamId] = requestId
    return requestId
  }
  const { getAvailableAdditionalColumnsFromRow, getNormalizedCurveTableHeaders, getSelectedAdditionalColumns } = additionalColumnsCustomInsightActions
  const DEFAULT_CURVE_KEYS = [{
    symbolKey: basicCustomInsightConsts.FORWARD_CURVE_KEY.ROOT,
    symbolType: DataStorageEnums.KEY_TYPE.METADATA,
    withAll: false,
    label: "Root"
  }]
  const ADD_INPUT_PARAMS = 'ADD_INPUT_PARAMS_CUSTOM_INSIGHT'
  const addInputParams = ActionService.makeActionCreator(ADD_INPUT_PARAMS)

  const DUPLICATE_INPUT_PARAMS = 'DUPLICATE_INPUT_PARAMS_CUSTOM_INSIGHT'
  const duplicateInputParams = () => (dispatch, getState) => {
    const activeInputParamsId = getState().insightState.customState.activeInputParamsId
    const inputParamsConfig = getInputParamsConfig({ state: getState(), id: activeInputParamsId })
    const newTitle = basicCustomInsightActions.getNewTileName(Object.values(getState().insightState.customState.content?.inputParams || {}), inputParamsConfig.title)
    dispatch(duplicateInputParamsReducer(activeInputParamsId, newTitle))
  }
  const duplicateInputParamsReducer = ActionService.makeActionCreator(DUPLICATE_INPUT_PARAMS, 'id', 'newTitle')
  
  const DELETE_INPUT_PARAM = 'DELETE_INPUT_PARAM_CUSTOM_INSIGHT'
  const deleteInputParam = ActionService.makeActionCreator(DELETE_INPUT_PARAM, 'inputId')

  const ON_CHANGE_INPUT_PARAMS_CONFIG = 'ON_CHANGE_INPUT_PARAMS_CONFIG_INSIGHT'
  const onChangeInputParamsConfig = ActionService.makeActionCreator(ON_CHANGE_INPUT_PARAMS_CONFIG, 'value', 'name', 'inputId')

  const ON_CHANGE_INPUT_PARAMS = 'ON_CHANGE_INPUT_PARAMS_CUSTOM_INSIGHT'
  const onChangeInputParamsReducer = ActionService.makeActionCreator(ON_CHANGE_INPUT_PARAMS, 'value', 'field', 'name')

  const ON_CHANGE_INPUT_PARAMS_LIST = 'ON_CHANGE_INPUT_PARAMS_LIST_CUSTOM_INSIGHT'
  const onChangeInputParamsList = ActionService.makeActionCreator(ON_CHANGE_INPUT_PARAMS_LIST, 'value', 'name', 'listName', 'listIndex', 'inputId')
  const getAvailablePresetLabel = ({ presets, label, excludedIndex, firstSuffix = 1 }) => {
    const usedLabels = new Set(presets.filter((_, index) => index !== excludedIndex).map(preset => preset.label))
    if(!usedLabels.has(label)) {
      return label
    }
    let suffix = firstSuffix
    while(usedLabels.has(`${label} ${suffix}`)) {
      suffix += 1
    }
    return `${label} ${suffix}`
  }
  const getNewPresetLabel = ({ presets }) => getAvailablePresetLabel({ presets, label: 'Preset', firstSuffix: 2 })

  const ON_CHANGE_INPUT_PARAMS_USER_SELECTION = 'ON_CHANGE_INPUT_PARAMS_USER_SELECTION_CUSTOM_INSIGHT'
  const onChangeInputParamsUserSelection = ActionService.makeActionCreator(ON_CHANGE_INPUT_PARAMS_USER_SELECTION, 'value', 'name', 'inputId')
  const getEffectiveDateRange = ({ config }) => config?.dataRange ?? DataStorageEnums.DATA_RANGE.BETWEEN
  const isDatePresetInputConfig = config => !config?.basicInputParamsType || config.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.TS.value
  const areDatePresetListsCurrent = ({ currentPresets, presets }) => currentPresets === presets || (!currentPresets && presets.length === 0)
  const isSupportedDatePreset = ({ preset, dataRange }) => {
    if(preset?.presetType !== INPUT_PARAMS_PRESET_TYPES.DATE_RANGE.value) {
      return false
    }
    const { dateType, formula, fromFormula, toFormula } = preset,
          isSpecificDate = dataRange === basicCustomInsightConsts.CUSTOM_DATE_RANGES.SPECIFIC_DATE.value,
          isSupportedType = isSpecificDate
            ? INPUT_PARAMS_SINGLE_DATE_RANGE_TYPES.includes(dateType)
            : Object.values(INPUT_PARAMS_DATE_RANGE_TYPES).includes(dateType)
    if(!isSupportedType) {
      return false
    }
    return dateType !== INPUT_PARAMS_DATE_RANGE_TYPES.FORMULA || (isSpecificDate
      ? Boolean((formula ?? fromFormula)?.trim())
      : Boolean((fromFormula ?? formula)?.trim() && toFormula?.trim()))
  }
  const isSupportedDateRange = dataRange => dataRange === DataStorageEnums.DATA_RANGE.BETWEEN
    || dataRange === basicCustomInsightConsts.CUSTOM_DATE_RANGES.SPECIFIC_DATE.value
  const isDatePresetMatchCurrent = ({ current, inputId, selection, presets, config, dataRange, requestId }) => {
    const currentSelection = current.inputParamsUserSelection?.[inputId],
          currentConfig = current.content.inputParams?.[inputId],
          currentPresets = currentConfig?.presets

    return current.activeInputParamsId === inputId
      && currentSelection?.datePreset === selection?.datePreset
      && currentSelection?.fromDate === selection?.fromDate
      && currentSelection?.toDate === selection?.toDate
      && currentSelection?.specificDate === selection?.specificDate
      && (latestDatePresetRequestByInputId.get(inputId) || 0) === requestId
      && currentConfig === config
      && areDatePresetListsCurrent({ currentPresets, presets })
      && currentConfig?.withDateRange !== false
      && isDatePresetInputConfig(currentConfig)
      && getEffectiveDateRange({ config: currentConfig }) === dataRange
      && isSupportedDateRange(dataRange)
  }
  const updateMatchingDatePreset = ({ inputId, matchingPreset, presets }) => (dispatch, getState) => {
    const current = getState().insightState.customState,
          selection = current.inputParamsUserSelection?.[inputId] || {},
          config = current.content.inputParams?.[inputId],
          dataRange = getEffectiveDateRange({ config }),
          requestId = latestDatePresetRequestByInputId.get(inputId) || 0,
          updateMatch = nextPreset => {
            const current = getState().insightState.customState
            if(!isDatePresetMatchCurrent({ current, inputId, selection, presets, config, dataRange, requestId })) {
              return
            }
            if(nextPreset !== selection?.datePreset) {
              dispatch(onChangeInputParamsUserSelection(nextPreset, 'datePreset', inputId))
            }
          }

    return matchingPreset?.then ? matchingPreset.then(updateMatch) : updateMatch(matchingPreset)
  }
  const isDatePresetSelectionCurrent = ({ current, inputId, preset, presets, requestId, selection, config, dataRange }) => {
    const currentSelection = current.inputParamsUserSelection?.[inputId],
          currentConfig = current.content?.inputParams?.[inputId],
          currentPresets = currentConfig?.presets

    return latestDatePresetRequestByInputId.get(inputId) === requestId
      && current.activeInputParamsId === inputId
      && currentSelection?.datePreset === preset
      && currentSelection?.fromDate === selection?.fromDate
      && currentSelection?.toDate === selection?.toDate
      && currentSelection?.specificDate === selection?.specificDate
      && currentConfig === config
      && areDatePresetListsCurrent({ currentPresets, presets })
      && currentConfig?.withDateRange !== false
      && isDatePresetInputConfig(currentConfig)
      && getEffectiveDateRange({ config: currentConfig }) === dataRange
      && isSupportedDateRange(dataRange)
  }
  const hasDatePresetRangeError = ({ dates, preset }) => Boolean(dates?.errorsByRowId)
    || (!dates && [INPUT_PARAMS_DATE_RANGE_TYPES.FORMULA, INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_BUSINESS_DAY].includes(preset?.dateType))
  const applyDatePresetDates = ({ dispatch, inputId, name, preset, dates, dataRange }) => {
    dispatch(onChangeInputParamsUserSelection(preset, name, inputId))
    if(dates) {
      if(dataRange === basicCustomInsightConsts.CUSTOM_DATE_RANGES.SPECIFIC_DATE.value) {
        dispatch(onChangeInputParamsUserSelection(dates.specificDate, 'specificDate', inputId))
      } else {
        dispatch(onChangeInputParamsUserSelection(dates.fromDate, 'fromDate', inputId))
        dispatch(onChangeInputParamsUserSelection(dates.toDate, 'toDate', inputId))
      }
    } else if(name === 'datePreset' && !preset) {
      if(dataRange === basicCustomInsightConsts.CUSTOM_DATE_RANGES.SPECIFIC_DATE.value) {
        dispatch(onChangeInputParamsUserSelection('', 'specificDate', inputId))
      } else {
        dispatch(onChangeInputParamsUserSelection('', 'fromDate', inputId))
        dispatch(onChangeInputParamsUserSelection('', 'toDate', inputId))
      }
    }
  }
  const selectDatePreset = ({ inputId, name, preset, previousPreset, dates, presets }) => (dispatch, getState) => {
    const requestId = (latestDatePresetRequestByInputId.get(inputId) || 0) + 1
    latestDatePresetRequestByInputId.set(inputId, requestId)

    const current = getState().insightState.customState,
          config = current.content.inputParams?.[inputId],
          dataRange = getEffectiveDateRange({ config }),
          currentPresets = config?.presets,
          presetList = presets || currentPresets || []

    if(name !== 'datePreset'
      || current.activeInputParamsId !== inputId
      || config?.withDateRange === false
      || !isDatePresetInputConfig(config)
      || !isSupportedDateRange(dataRange)
      || preset && !isSupportedDatePreset({ preset, dataRange })
      || !areDatePresetListsCurrent({ currentPresets, presets: presetList })) {
      return
    }

    const currentSelection = current.inputParamsUserSelection?.[inputId] || {},
          selection = {
            fromDate: currentSelection.fromDate,
            toDate: currentSelection.toDate,
            specificDate: currentSelection.specificDate
          }

    if(dates?.then) {
      dispatch(onChangeInputParamsUserSelection(preset, name, inputId))

      return dates.then(resolvedDates => {
        const current = getState().insightState.customState
        if(!isDatePresetSelectionCurrent({ current, inputId, preset, presets: presetList, requestId, selection, config, dataRange })) {
          return
        }
        if(!hasDatePresetRangeError({ dates: resolvedDates, preset })) {
          applyDatePresetDates({ dispatch, inputId, name, preset, dates: resolvedDates, dataRange })
          return
        }
        dispatch(onChangeInputParamsUserSelection(previousPreset, name, inputId))
        return { hasError: true, result: resolvedDates }
      })
    }
    if(hasDatePresetRangeError({ dates, preset })) {
      return { hasError: true, result: dates }
    }
    applyDatePresetDates({ dispatch, inputId, name, preset, dates, dataRange })
  }
  const hasScopeValue = value => ReduxService.nonNullOrUndefined(value)
    && value !== ''
    && (!Array.isArray(value) || value.length > 0)
  const hasOwn = ({ object, key }) => Object.prototype.hasOwnProperty.call(object || {}, key)
  const getOverwrittenInputParamValue = ({ fallbackValue, includeMetadata = false, key, overwrittenInputParameters }) => {
    if(hasOwn({ object: overwrittenInputParameters.symbols, key })) {
      return overwrittenInputParameters.symbols[key]
    }
    if(includeMetadata && hasOwn({ object: overwrittenInputParameters.metadatas, key })) {
      return overwrittenInputParameters.metadatas[key]
    }
    return fallbackValue
  }
  const getScopePresetSymbols = ({ keys, values, symbols, preset }) => {
    const changedKeys = keys.filter(key => !key.isHidden && key.symbolKey && hasScopeValue(values[key.symbolKey]))
    return {
      changedKeys,
      nextSymbols: ReduxService.updateObject(symbols, Object.fromEntries(changedKeys.map(key => [key.symbolKey, preset ? values[key.symbolKey] : []])))
    }
  }
  const getScopePresetFileColumns = ({ configuredColumns, values, fileColumns, preset }) => {
    const isDateColumn = column => column.columnType === FILE_INPUT_PARAMS_VALUE_TYPES.DATE.value,
          hasCompatibleValue = column => isDateColumn(column)
            ? hasOwn({ object: values[column.column], key: 'valueFrom' }) || hasOwn({ object: values[column.column], key: 'valueTo' })
            : hasOwn({ object: values[column.column], key: 'value' }),
          changedColumns = configuredColumns.filter(column => !column.isHidden && hasOwn({ object: values, key: column.column }) && hasCompatibleValue(column)),
          nextFileColumns = { ...fileColumns }

    changedColumns.forEach(column => {
      if(preset) {
        nextFileColumns[column.column] = isDateColumn(column)
          ? { ...nextFileColumns[column.column], valueFrom: values[column.column].valueFrom ?? '', valueTo: values[column.column].valueTo ?? '' }
          : { ...nextFileColumns[column.column], value: values[column.column].value }
      } else if(isDateColumn(column)) {
        nextFileColumns[column.column] = { ...nextFileColumns[column.column], valueFrom: '', valueTo: '' }
      } else {
        nextFileColumns[column.column] = { ...nextFileColumns[column.column], value: [] }
      }
    })

    return { changedColumns, nextFileColumns }
  }
  const getScopePresetBaseline = ({ configuredValues, personalValues, currentValues, defaultValueName }) => Object.fromEntries(configuredValues.flatMap(value => {
    const name = value.symbolKey || value.column
    if(!name) {
      return []
    }
    if(value.symbolKey) {
      const hasCurrentValue = hasOwn({ object: currentValues, key: name }),
            hasPersonalValue = hasOwn({ object: personalValues, key: name }),
            personalValue = personalValues?.[name],
            currentValue = currentValues?.[name],
            resolvedValue = hasCurrentValue
              ? currentValue
              : hasPersonalValue
                ? personalValue
                : value[defaultValueName]

      return resolvedValue === undefined && !hasCurrentValue && !hasPersonalValue ? [] : [[name, resolvedValue]]
    }

    const { defaultValue, defaultValueFrom, defaultValueTo, ...columnProps } = value,
          defaultSelection = value.columnType === FILE_INPUT_PARAMS_VALUE_TYPES.DATE.value
            ? { valueFrom: defaultValueFrom ?? '', valueTo: defaultValueTo ?? '' }
            : { value: defaultValue ?? value[defaultValueName] },
          personalValue = personalValues?.[name] || {},
          currentValue = currentValues?.[name] || {}

    return [[name, { ...columnProps, ...defaultSelection, ...personalValue, ...currentValue }]]
  }))
  const getScopePresetFilters = ({ configuredValues, filterValues, selection, personalDefaults, preset }) => {
    const changedFilterIndexes = new Set(configuredValues.flatMap((value, index) => !value.isHidden && value.column && value.operator && value.type && hasScopeValue(filterValues[index]) ? [index] : [])),
          nextFilters = configuredValues.map((value, index) => {
            const userFilter = selection.filters?.find(filter => filter.index === index)
                  || selection.filters?.find(filter => !ReduxService.nonNullOrUndefined(filter.index) && filter.name === value.column),
                  savedFilter = personalDefaults.filters?.find(filter => filter.index === index)
                    || personalDefaults.filters?.find(filter => !ReduxService.nonNullOrUndefined(filter.index) && filter.name === value.column)
            return {
              ...savedFilter,
              ...userFilter,
              index,
              name: value.column,
              operator: value.operator,
              value: changedFilterIndexes.has(index) ? preset ? filterValues[index] : '' : userFilter?.value ?? savedFilter?.value ?? value.defaultValue
            }
          })
    return { changedFilterIndexes, nextFilters }
  }
  const selectScopePreset = ({ inputId, preset }) => (dispatch, getState) => {
    const state = getState(),
          current = state.insightState.customState,
          inputConfig = current.content.inputParams?.[inputId] || {},
          selection = current.inputParamsUserSelection?.[inputId] || {},
          personalDefaults = CommonService.parseJson(state.myAccountState?.settingsState?.[`inputParams_${inputId}`], {}),
          inputType = inputConfig.basicInputParamsType || BASIC_INPUT_PARAM_TYPES.TS.value,
          keys = inputType === BASIC_INPUT_PARAM_TYPES.CURVE.value ? inputConfig.curveKeys || [] : inputConfig.keys || [],
          configuredValues = inputType === BASIC_INPUT_PARAM_TYPES.TS.value ? inputConfig.values || [] : [],
          configuredColumns = inputType === BASIC_INPUT_PARAM_TYPES.FILE.value ? inputConfig.fileColumns || [] : [],
          sourcePreset = preset || selection[basicCustomInsightConsts.INPUT_PARAMS_SCOPE_PRESET_FIELDS.SELECTION],
          values = sourcePreset?.[basicCustomInsightConsts.INPUT_PARAMS_SCOPE_PRESET_FIELDS.VALUES] || {},
          filterValues = sourcePreset?.[basicCustomInsightConsts.INPUT_PARAMS_SCOPE_PRESET_FIELDS.FILTER_VALUES] || {},
          fileColumnValues = sourcePreset?.[basicCustomInsightConsts.INPUT_PARAMS_SCOPE_PRESET_FIELDS.FILE_COLUMN_VALUES] || {},
          symbolsBaseline = getScopePresetBaseline({
            configuredValues: keys,
            personalValues: personalDefaults.symbols,
            currentValues: selection.symbols,
            defaultValueName: 'defaultValue'
          }),
          { changedKeys, nextSymbols } = getScopePresetSymbols({ keys, values, symbols: symbolsBaseline, preset }),
          { changedFilterIndexes, nextFilters } = getScopePresetFilters({ configuredValues, filterValues, selection, personalDefaults, preset }),
          fileColumnsBaseline = getScopePresetBaseline({
            configuredValues: configuredColumns,
            personalValues: personalDefaults.fileColumns,
            currentValues: selection.fileColumns,
            defaultValueName: 'defaultValue'
          }),
          { changedColumns, nextFileColumns } = getScopePresetFileColumns({
            configuredColumns,
            values: fileColumnValues,
            fileColumns: fileColumnsBaseline,
            preset
          })

    if([BASIC_INPUT_PARAM_TYPES.TS.value, BASIC_INPUT_PARAM_TYPES.CURVE.value].includes(inputType)) {
      dispatch(onChangeInputParamsUserSelection(nextSymbols, 'symbols', inputId))
      changedKeys.forEach(key => dispatch(changeOtherInputParams({ value: preset ? values[key.symbolKey] : [], label: key.label, inputId })))
    } else if(inputType === BASIC_INPUT_PARAM_TYPES.FILE.value) {
      dispatch(onChangeInputParamsUserSelection(nextFileColumns, 'fileColumns', inputId))
      changedColumns.forEach(column => {
        const value = nextFileColumns[column.column] || {}
        dispatch(changeOtherInputParams({ value: value.value, valueFrom: value.valueFrom, valueTo: value.valueTo, label: column.label, inputId }))
      })
    } else {
      return
    }
    if(changedFilterIndexes.size > 0) {
      dispatch(onChangeInputParamsUserSelection(nextFilters, 'filters', inputId))
    }
    dispatch(onChangeInputParamsUserSelection(preset, basicCustomInsightConsts.INPUT_PARAMS_SCOPE_PRESET_FIELDS.SELECTION, inputId))
  }
  const changeOtherInputParams = ({ value, valueFrom, valueTo, label, inputId, isRawParam = false }) => (dispatch, getState) => {
    const inputParams = getState().insightState.customState.content?.inputParams,
          inputParamsUserSelection = getState().insightState.customState?.inputParamsUserSelection,
          sourceInputParams = getState().insightState.customState.content?.inputParams?.[inputId],
          basicInputParamsType = sourceInputParams?.basicInputParamsType,
          sourceSqlBinding = sourceInputParams?.sqlBindings?.find(binding => binding.name === label)
    const synchronizedInputParams = []
    Object.entries(inputParams || {}).filter(([inputParamsId, inputParamsContent]) => inputParamsId !== inputId && inputParamsContent.shouldSynchronizeParams).forEach(([inputParamsId, inputParamsContent]) => {
      const { keys = [], fileColumns = [], curveKeys = DEFAULT_CURVE_KEYS, sqlBindings = [], basicInputParamsType } = inputParamsContent
      const commonProps = { id: inputParamsId, value, label },
            matchedFileColumn = fileColumns.find(fc => fc.label === label && !isRawParam), 
            matchedKey = keys.find(k => k.label === label && !isRawParam),
            matchedCurveKey = curveKeys.find(ck => ck.label === label && !isRawParam),
            matchedSqlBinding = sqlBindings.find(binding => binding.name === label && binding.valueType === sourceSqlBinding?.valueType)
      if(basicInputParamsType === BASIC_INPUT_PARAM_TYPES.FILE.value && matchedFileColumn) {
        synchronizedInputParams.push({ ...commonProps, valueFrom, valueTo, name: matchedFileColumn.column, type: "fileColumns" })
      } else if(basicInputParamsType === BASIC_INPUT_PARAM_TYPES.TS.value && matchedKey) {
        synchronizedInputParams.push({ ...commonProps, name: matchedKey.symbolKey, type: "symbols" })
      } else if(basicInputParamsType === BASIC_INPUT_PARAM_TYPES.CURVE.value && matchedCurveKey) {
        synchronizedInputParams.push({ ...commonProps, name: matchedCurveKey.symbolKey, type: "symbols" })
      } else if(isRawParam && sourceSqlBinding && basicInputParamsType === BASIC_INPUT_PARAM_TYPES.SQL.value && inputParamsContent.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.SQL.value && matchedSqlBinding) {
        synchronizedInputParams.push({ ...commonProps, name: matchedSqlBinding.name, type: "sqlValues" })
      } else if(isRawParam && basicInputParamsType === inputParamsContent.basicInputParamsType) {
        synchronizedInputParams.push({ ...commonProps, name: label, type: "isRawValue" })
      }
    })
    synchronizedInputParams.forEach(({ id, value, valueFrom, valueTo, name, type }) => {
      const userSelection = inputParamsUserSelection[id] || {},
            toBeUpdated = userSelection[type] || {}
      if(type === "symbols") {
        dispatch(onChangeInputParamsUserSelection(ReduxService.updateObject(toBeUpdated, { [name]: value } ), type, id))
      } else if(type === "fileColumns") {
        dispatch(onChangeInputParamsUserSelection(ReduxService.updateObject(toBeUpdated, { [name]: { value, valueFrom, valueTo } }), type, id))
      } else if(type === "sqlValues") {
        const sqlValues = SqlSourceService.applyBindingValueChange({ values: toBeUpdated, name, value })
        dispatch(onChangeInputParamsUserSelection(sqlValues, type, id))
      } else if(type === "isRawValue" && basicInputParamsType) {
        dispatch(onChangeInputParamsUserSelection(value, name, id))
      }
    })
  }

  const ON_ADD_INPUT_PARAMS_LIST = 'ON_ADD_INPUT_PARAMS_LIST_CUSTOM_INSIGHT'
  const onAddInputParamsList = ActionService.makeActionCreator(ON_ADD_INPUT_PARAMS_LIST, 'item', 'listName', 'inputId')

  const ON_REMOVE_INPUT_PARAMS_LIST = 'ON_REMOVE_INPUT_PARAMS_LIST_CUSTOM_INSIGHT'
  const onRemoveInputParamsList = ActionService.makeActionCreator(ON_REMOVE_INPUT_PARAMS_LIST, 'listName', 'listIndex', 'inputId')

  const ON_REORDER_INPUT_PARAMS_LIST = 'ON_REORDER_INPUT_PARAMS_LIST_CUSTOM_INSIGHT'
  const onReorderInputParamsList = ActionService.makeActionCreator(ON_REORDER_INPUT_PARAMS_LIST, 'listName', 'from', 'to', 'inputId')

  const IMPORT_INPUT_PARAMS_REPLACE_TILE = 'IMPORT_INPUT_PARAMS_REPLACE_TILE'
  const importInputParamsReplaceTile = ActionService.makeActionCreator(IMPORT_INPUT_PARAMS_REPLACE_TILE, 'oldId', 'newId', 'config')

  const parseResultForTable = ({ additionalColumns = [], shouldNormalizeAdditionalColumns = false, parseCsv, response, onError, onHandleRequest  }) => {
    parse(response, {
      download: false,
      complete: result => {
        const selectedAdditionalColumns = getSelectedAdditionalColumns({
                availableAdditionalColumns: getAvailableAdditionalColumnsFromRow({ row: result?.data?.[1], fromIndex: 1 }),
                columns: additionalColumns
              }),
              header = shouldNormalizeAdditionalColumns && selectedAdditionalColumns.length > 1
                ? getNormalizedCurveTableHeaders({
                    additionalColumns: selectedAdditionalColumns,
                    rows: result.data
                  })
                : result.data?.[0] || []

        parseCsv(response.replace(unparse(result), header.join(",")))
      },
      error: error => {
        onError?.()
        console.error(error)
        onHandleRequest({ isResponse: true })
        return Promise.resolve()
      },
      skipEmptyLines: true,
      preview: 1
    })
  }

  const parseResultsForChart = ({ isRelative, labelDateFormat, leftAxisData, newResult, periodType, rightAxisData, symbolSize }) => {
    const rowIndexes = basicCustomInsightConsts.getCurveChartCsvRowIndexes(newResult),
          groupRow = newResult[rowIndexes.GROUP_NAME] || [],
          rootRow = newResult[rowIndexes.FUTURES_ROOT] || [],
          productRow = newResult[rowIndexes.PRODUCT] || [],
          columnRow = newResult[rowIndexes.SYMBOL_COLUMN] || [],
          dateRow = newResult[rowIndexes.DATE] || [],
          columns = {}
      let lastFullObject, index = 0;
      groupRow.forEach((groupName, columnIndex) => {
        const object = {
          groupName: groupName || lastFullObject?.groupName,
          root: rootRow[columnIndex] || lastFullObject?.root,
          product: productRow[columnIndex] || lastFullObject?.product,
          column: columnRow[columnIndex] || lastFullObject?.column,
          date: dateRow[columnIndex] || lastFullObject?.date,
        }
        if(columnIndex > 0 && (object.root !== lastFullObject?.root || object.groupName !== lastFullObject?.groupName)) {
          index = 0;
        } else if(columnIndex > 0) {
          index++;
        }
        if(columnIndex !== 0) {
          lastFullObject = object;
        }
        const dataObject = (leftAxisData || []).map(d => ReduxService.updateObject(d, { right: false })).concat((rightAxisData || []).map(d => ReduxService.updateObject(d, { right: true }))).find(d => 
            d.groupName === object.groupName &&
            d.root === object.root &&
            d.column === object.column
          )
        const symbol = buildCurveSymbol({ date: object.date, index, isRelative, periodType, root: object.root, symbolSize })
        columns[columnIndex] = {
          ...object,
          ...(symbol ? { symbols: { [basicCustomInsightConsts.FORWARD_CURVE_KEY.SYMBOL]: symbol } } : {}),
          csvIndex: columnIndex,
          index,
          dataObject: dataObject || object,
          fullLabel: object.date ? (dataObject?.label || object.root) + " | " + (labelDateFormat ? dayjs(object.date, TimeService.translateFormat(TimeService.ISO_DATE_TIME_FORMAT)).format(TimeService.translateFormat(labelDateFormat || TimeService.ISO_DATE_TIME_FORMAT)) : object.date) : undefined
        }
      })
      return {
        data: 
          newResult.slice(rowIndexes.DATE + 1).map(row =>
            Object.fromEntries(
              row.map((cell, columnIndex) => [
                columnIndex === 0 ? "time" : columns[columnIndex]?.fullLabel,
                cell]
              ).filter(r => r[0] !== undefined)
            )
          ),
        // Column 0 of the curve CSV holds the row names ("Group name", "FuturesRoot", "Dates", ...), not a curve.
        // Kept as a curve it became an empty series and a "Group name" group in the insight roles ("Group not found." on save)
        columns: Object.values(columns).filter(c => c.csvIndex > 0 && c.date !== undefined)
      }
  }
  // All providers: every environment of the input params group (groupSources), [undefined] when none were saved
  const getGroupSources = ({ inputParams }) => {
    const sources = (inputParams?.groupSources || [])
      .filter(groupSource => groupSource.groupName === inputParams.groupName && groupSource.source)
      .map(groupSource => groupSource.source)

    return sources.length > 0 ? sources : [undefined]
  }

  // All providers: every curve item goes to every environment of the group, /ts/stream/curve/envs reads it from its source
  const getCurveItemsWithSources = ({ inputParams }) => {
    const items = getCurveItemsFromInputParams({ inputParams })

    return basicCustomInsightConsts.isAllProviders(inputParams.provider)
      ? items.flatMap(item => getGroupSources({ inputParams }).map(source => ReduxService.updateObject(item, { source })))
      : items
  }

  const getCurveInputColumns = ({ inputParams }) => {
    const columns = Array.isArray(inputParams.columns) && inputParams.columns.length > 0 ? inputParams.columns : [inputParams.column]

    return columns.filter(Boolean)
  }
  const getCurveItemsFromInputParams = ({ inputParams }) =>
    (inputParams.curveKeys || [])
      .filter(curveKey => curveKey.symbolKey === basicCustomInsightConsts.FORWARD_CURVE_KEY.ROOT)
      .flatMap(curveKey => {
        const roots = Array.isArray(inputParams.metadatas?.[curveKey.symbolKey]) ? inputParams.metadatas[curveKey.symbolKey] : [inputParams.metadatas?.[curveKey.symbolKey]],
              columns = getCurveInputColumns({ inputParams })

        return roots
          .filter(Boolean)
          .flatMap(root => columns.map(column => ({ root, groupName: inputParams.groupName, column })))
      })
  const getCurveBodyKeys = ({ items = [] }) =>
    items.filter(item => item.root && item.groupName && item.column).map(item =>
      new CurveKey.Builder()
        .withRoot(item.root)
        .withGroupName(item.groupName)
        .withColumn(item.column)
        // Curves with All providers (search tile or input params) have it: the environment of the key for /ts/stream/curve/envs
        .withSource(item.source)
        .build()
    )

  const getCurveBody = ({ keys, passLabelDateFormatToCall, labelDateFormat, contractSort, fillBlanks, periodType, symbolSize, isRelative, timeZone, timezone, csvHeaders, range, from, to, specificDate, fromSelected, toSelected, lastType, lastTypeAmount, dateFormat }) => {
    let builder = 
      new GetCurveBody.Builder()
        .withKeys(keys)
        .withContractSort(contractSort)
        .withDateFormat(passLabelDateFormatToCall ? labelDateFormat : dateFormat)
        .withFillBlanks(fillBlanks)
        .withPeriodType(periodType)
        .withLimitContracts(symbolSize)
        .withRelative(isRelative === "true")
        .withTimezone(timeZone || timezone)
        .withCsvHeaders((Array.isArray(csvHeaders) ? csvHeaders : []).map(c => ({ name: c.value, type: c.type })))
    if(range === basicCustomInsightConsts.CUSTOM_DATE_RANGES.TODAY.value) {
      builder = builder
        .withStartDate(DataStorageEnums.DATE_BETWEEN_TYPE.TODAY)
        .withEndDate(DataStorageEnums.DATE_BETWEEN_TYPE.TODAY)
        .withRange(DataStorageEnums.DATA_RANGE.BETWEEN)
    } else if(range === DataStorageEnums.DATA_RANGE.BETWEEN) {
      const startDateValue = fromSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE && from ? dayjs.utc(from + "T00:00:00.000").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : fromSelected
      const endDateValue = toSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE && to ? dayjs.utc(to + "T23:59:59.999").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : toSelected
      builder = builder
              .withStartDate(startDateValue === "undefined" || startDateValue === "Invalid Date" ? undefined : startDateValue)
              .withEndDate(endDateValue === "undefined" || endDateValue === "Invalid Date" ? undefined : endDateValue)
              .withRange(range)
    } else if(range === DataStorageEnums.DATA_RANGE.LAST || range === DataStorageEnums.DATA_RANGE.LAST) {
      builder = builder
              .withLastType(lastType)
              .withLastTypeAmount(lastTypeAmount)
              .withRange(range)
    } else if(range === basicCustomInsightConsts.CUSTOM_DATE_RANGES.SPECIFIC_DATE.value) {
      const specificDateFrom = specificDate ? dayjs.utc(specificDate + "T00:00:00.000").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined,
            specificDateTo = specificDate ? dayjs.utc(specificDate + "T23:59:59.999").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined
      builder = builder
              .withStartDate(specificDateFrom === "undefined" || specificDateFrom === "Invalid Date" ? undefined : specificDateFrom)
              .withEndDate(specificDateTo === "undefined" || specificDateTo === "Invalid Date" ? undefined : specificDateTo)
              .withRange(DataStorageEnums.DATA_RANGE.BETWEEN)
    } else {
      builder = builder.withRange(range)
    }
    return builder.build()
  }

  const getCurveData = ({ curveBody, getCurvesDataCall, onHandleRequest, onError, onComplete, header, preparsing = ({ parseCsv, response }) => parseCsv(response) }) => dispatch => {
    onHandleRequest({ isResponse: false })
    return getCurvesDataCall(curveBody)
      .then(response => response.text())
      .then(response => {
        const parseCsv = response => {
          parse(response, {
              download: false,
              complete: result => {
                const newResult = result?.data || []
                if(newResult[newResult?.length - 1]?.length !== newResult[0]?.length) {
                  newResult.pop()
                }
                if(result?.errors.length !== 0) {
                  if(result?.data?.length === 0){
                    dispatch(globalActions.getMessageStore()).error("No data has been received.")
                  }
                  onHandleRequest({ isResponse: true })
                  return onError ? onError({ error: result?.errors, onError, onHandleRequest }) : Promise.resolve()
                } else {
                  onComplete({ data: newResult, columns: result?.meta?.fields || [] })
                  onHandleRequest({ isResponse: true })
                }
              },
              error: error => {
                onError?.()
                console.error(error)
                onHandleRequest({ isResponse: true })
                return Promise.resolve()
              },
              skipEmptyLines: true,
              header,
              worker: true,
              preview: basicCustomInsightConsts.ABSOLUTE_SIZE
          })
        }
        preparsing({ parseCsv, response, onError, onHandleRequest, dispatch })
        return response;
      })
      .catch(error => {
        if(error.message !== "Request aborted. No action needed.") {
          onError?.()
          console.error(error)
          onHandleRequest({ isResponse: true })
          return Promise.resolve()
        }
      })
  }

  // Promise form of the table-tile curve request for callers outside an insight, e.g. the Datalake Review output preview.
  const fetchCurveTableData = ({ curveBody, getCurvesDataCall }) => dispatch => new Promise((resolve, reject) => {
    dispatch(getCurveData({
      curveBody,
      getCurvesDataCall,
      header: true,
      onComplete: resolve,
      onError: () => reject(new Error(CURVE_DATA_ERROR_MESSAGE)),
      onHandleRequest: () => {},
      preparsing: parseResultForTable
    }))
  })

  const getRequestIds = ({ specificTiles, prefix, tiles }) => 
    specificTiles.map(outerTile => {
      const tileIndex = tiles.findIndex(tile => tile.id === outerTile.id)
      return prefix + tileIndex + "-" + CommonService.generateUID();
    })

  const executeTimeSeriesInputParams = ({ areThereAnyChartTiles, areThereAnyStatsTiles, areThereAnyTableTiles, withMessage, tiles, statsTiles, inputParamId, inputParams, getDataCall, chartTiles, tableTiles }) => dispatch => {
    if(areThereAnyStatsTiles) {
      const requestIds = getRequestIds({ specificTiles: statsTiles, prefix: basicCustomInsightConsts.TILE_TYPES.STATS, tiles })
      const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
      const onError = () => {
        dispatch(onChangeInputParamsReducer({ symbols: {}, values: [], stats: [], metadata: {}, isError: true }, "stats", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
      }
      const onComplete = ({ symbols, metadata, stats }) => {
        dispatch(onChangeInputParamsReducer({ symbols, metadata, stats, isError: false }, "stats", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
        if(!areThereAnyChartTiles && !areThereAnyTableTiles && withMessage) {
          dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
        }
      }
      chartCustomInsightActions.getSourceTileData({ onHandleRequest, inputParams, onComplete, stats: true, onError, getDataCall })
    }
    if(areThereAnyTableTiles) {
      const requestIds = getRequestIds({ specificTiles: tableTiles, prefix: basicCustomInsightConsts.TILE_TYPES.TABLE, tiles })
      const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
      const onError = () => {
        dispatch(onChangeInputParamsReducer({ columns: [], data: [], isError: true }, "table", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
      }
      const onComplete = ({ columns, data }) => {
        dispatch(onChangeInputParamsReducer({ columns, data, isError: false }, "table", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
        if(!areThereAnyChartTiles && withMessage) {
          dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
        }
      }
      const isAllProviders = basicCustomInsightConsts.isAllProviders(inputParams.provider)
      dispatch(tableCustomInsightActions.getSourceTileData({
        onHandleRequest,
        inputParams,
        onComplete,
        onError,
        provider: basicCustomInsightConsts.getProviderTarget(inputParams.provider),
        useEnvs: isAllProviders,
        sources: isAllProviders ? getGroupSources({ inputParams }) : undefined
      }))
    }
    if(areThereAnyChartTiles) {
      const requestIds = getRequestIds({ specificTiles: chartTiles, prefix: basicCustomInsightConsts.TILE_TYPES.CHART, tiles })
      const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
      const onError = () => {
        dispatch(onChangeInputParamsReducer({ symbols: [], values: [], metadata: {}, isError: true }, "chart", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
      }
      const onComplete = ({ symbols, values, metadata }) => {
        dispatch(onChangeInputParamsReducer({ symbols, values, metadata: inputParams.withMetadata ? metadata || {} : {}, isError: false }, "chart", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
        if(withMessage) {
          dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
        }
      }
      chartCustomInsightActions.getSourceTileData({ onHandleRequest, inputParams, onComplete, getDataCall, onError })
    }
  }

  const executeFileInputParams = ({ chartTiles, tableTiles, tiles, inputParamId, inputParams, withMessage, searchFilesCall, getFileDetailsCall, inputParamsTitle }) => dispatch => {
    const requestIds = getRequestIds({ specificTiles: tableTiles, prefix: basicCustomInsightConsts.TILE_TYPES.TABLE, tiles }).concat(getRequestIds({ specificTiles: chartTiles, prefix: basicCustomInsightConsts.TILE_TYPES.CHART, tiles }))
    const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
    const onError = () => {
      dispatch(onChangeInputParamsReducer({ columns: [], filteredData: [], data: [], isError: true }, "fileData", inputParamId))
      dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
    }
    const onComplete = ({ columns, data }) => {
      const FILTER_WORKER = new Worker(new URL("./filterCsvWorker.js", import.meta.url))
      new Promise(resolve => {
        FILTER_WORKER.postMessage({ fileData: data, inputParams })
        FILTER_WORKER.onmessage = ({ data: { filteredData } }) => {
          dispatch(onChangeInputParamsReducer({ columns, data, filteredData, isError: false }, "fileData", inputParamId))
          dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
          resolve()
          if(withMessage) {
            dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
          }
        }
      }).finally(() => {
        FILTER_WORKER.terminate()
      })
    }
    dispatch(getInputParamFile({ 
      file: inputParams.csvFile, 
      onComplete, 
      onError, 
      onHandleRequest, 
      templateContent: inputParams.templateContent,
      searchFilesCall,
      getFileDetailsCall,
      inputParamsTitle
    }))
  }

  const executeCurvesInputParams = ({ inputParams, chartTiles, tiles, tableTiles, inputParamId, areThereAnyChartTiles, areThereAnyTableTiles, withMessage, getCurvesDataCall }) => dispatch => {
    const curveKeys = getCurveBodyKeys({ items: getCurveItemsWithSources({ inputParams }) }),
          curveRequestTarget = basicCustomInsightConsts.isAllProviders(inputParams.provider) ? { useEnvs: true } : { provider: inputParams.provider }
    if(curveKeys.length === 0) {
      return;
    }
    // If there are table tiles, execute the call with table csv headers set up properly
    if(areThereAnyTableTiles) {
      const requestIds = getRequestIds({ specificTiles: tableTiles, prefix: basicCustomInsightConsts.TILE_TYPES.TABLE, tiles })
      const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
      const onError = () => {
        dispatch(onChangeInputParamsReducer({ columns: [], filteredData: [], data: [], isError: true }, "fileData", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
      }
      const onComplete = ({ columns, data }) => {
        dispatch(onChangeInputParamsReducer({ columns, data, isError: false }, "table", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
        if(!areThereAnyChartTiles && withMessage) {
          dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
        }
      }
      const requestBody = ReduxService.updateObject(
        getCurveBody({ ...inputParams, keys: curveKeys, passLabelDateFormatToCall: true, csvHeaders: inputParams.csvHeaders || basicCustomInsightConsts.DEFAULT_CSV_HEADERS_CURVE_TABLE }),
        curveRequestTarget
      )
      dispatch(getCurveData({
        curveBody: requestBody,
        getCurvesDataCall,
        onHandleRequest,
        onComplete,
        onError,
        preparsing: args => parseResultForTable({
          ...args,
          additionalColumns: inputParams.columns,
          shouldNormalizeAdditionalColumns: !!inputParams.withColumnSelector
        }),
        header: true
      }))
    }
    // If there are table tiles, execute the call with chart csv headers set up properly
    if(areThereAnyChartTiles) {
      const requestIds = getRequestIds({ specificTiles: chartTiles, prefix: basicCustomInsightConsts.TILE_TYPES.CHART, tiles })
      const onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(reqId => dispatch(globalActions.handleRequest({ id: reqId, noFetching: true, isResponse })))
      const onError = () => {
        dispatch(onChangeInputParamsReducer({ columns: [], filteredData: [], data: [], isError: true }, "fileData", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
      }
      const onComplete = ({ data }) => {
        const { data: parsedData, columns: parsedColumns } = parseResultsForChart({ ...inputParams, newResult: data, labelDateFormat: "" })
        dispatch(onChangeInputParamsReducer({ columns: parsedColumns, data: parsedData, filteredData: parsedData , isError: false }, "fileData", inputParamId))
        dispatch(onChangeInputParamsReducer(inputParams, "inputParams", inputParamId))
        if(withMessage) {
          dispatch(globalActions.getMessageStore()).success("Parameters applied succesfully")
        }
      }
      const requestBody = ReduxService.updateObject(
        getCurveBody({ ...inputParams, keys: curveKeys, csvHeaders: basicCustomInsightConsts.DEFAULT_CSV_HEADERS_CURVE_CHART, passLabelDateFormatToCall: true }),
        curveRequestTarget
      )
      dispatch(getCurveData({ curveBody: requestBody, getCurvesDataCall, onHandleRequest, onComplete, onError, header: false, ...inputParams }))
    }
  }

  const getSqlRequestSnapshot = ({ inputParams }) => JSON.stringify({
    basicInputParamsType: inputParams.basicInputParamsType,
    sql: inputParams.sql,
    sqlBindings: inputParams.sqlBindings || [],
    sqlValues: inputParams.sqlValues || {}
  })

  const executeSqlInputParams = ({ inputParamId, inputParams, overwrittenInputParameters, chartTiles, tableTiles, tiles, withMessage, getDataFromSqlCall }) => async dispatch => {
    const bindings = inputParams.sqlBindings || [],
          sqlValues = inputParams.sqlValues || {},
          requestId = startSqlRequest(inputParamId),
          requestSnapshot = getSqlRequestSnapshot({ inputParams })

    if(!inputParams.sql?.trim() || !SqlSourceService.areBindingsComplete({ bindings, values: sqlValues })) {
      dispatch(onChangeInputParamsReducer({ columns: [], data: [], filteredData: [], isError: false }, 'fileData', inputParamId))
      dispatch(onChangeInputParamsReducer(inputParams, 'inputParams', inputParamId))
      return
    }

    const isCurrentRequest = () => {
            const currentInputParams = dispatch(getInputParamsConfigWithUserInput({
              sourceInputParamsId: inputParamId,
              overwrittenInputParameters
            })) || {}
            return latestSqlRequestByInputParam[inputParamId] === requestId
              && getSqlRequestSnapshot({ inputParams: currentInputParams }) === requestSnapshot
          },
          requestIds = getRequestIds({ specificTiles: tableTiles, prefix: TILE_TYPES.TABLE, tiles })
            .concat(getRequestIds({ specificTiles: chartTiles, prefix: TILE_TYPES.CHART, tiles }))
            .map(id => `${id}-sql-${requestId}`),
          onHandleRequest = ({ isResponse } = {}) => requestIds.forEach(id => dispatch(globalActions.handleRequest({ id, noFetching: true, isResponse })))

    onHandleRequest()

    try {
      const csv = await getDataFromSqlCall({ sql: inputParams.sql, parameters: sqlValues })
      if(!isCurrentRequest()) {
        return
      }

      const { columns, data } = SqlSourceService.parseCsv({ csv, maxRows: basicCustomInsightConsts.ABSOLUTE_SIZE })
      dispatch(onChangeInputParamsReducer({ columns, data, filteredData: data, isError: false }, 'fileData', inputParamId))
      dispatch(onChangeInputParamsReducer(inputParams, 'inputParams', inputParamId))
      if(withMessage) {
        dispatch(globalActions.getMessageStore()).success('Parameters applied succesfully')
      }
    } catch(error) {
      if(!isCurrentRequest()) {
        return
      }

      dispatch(onChangeInputParamsReducer({ columns: [], data: [], filteredData: [], isError: true }, 'fileData', inputParamId))
      dispatch(onChangeInputParamsReducer(inputParams, 'inputParams', inputParamId))
      dispatch(globalActions.getMessageStore()).error(SqlSourceService.getErrorMessage({ error }))
    } finally {
      onHandleRequest({ isResponse: true })
    }
  }

  const confirmSqlInputParams = ({ inputParamId, sql, sqlBindings: modalBindings, ...calls }) => (dispatch, getState) => {
    const state = getState(),
          currentConfig = state.insightState.customState.content?.inputParams?.[inputParamId] || {},
          sqlBindings = modalBindings
            ? SqlSourceService.mergeBindings({ sql, bindings: modalBindings })
            : SqlSourceService.reconcileBindings({ sql, bindings: currentConfig.sqlBindings || [] }),
          currentSqlValues = state.insightState.customState.inputParamsUserSelection?.[inputParamId]?.sqlValues || {},
          bindingNames = new Set(sqlBindings.map(binding => binding.name)),
          sqlValues = Object.fromEntries(Object.entries(currentSqlValues).filter(([name]) => bindingNames.has(name)))

    dispatch(onChangeInputParamsConfig(sql, 'sql', inputParamId))
    dispatch(onChangeInputParamsConfig(sqlBindings, 'sqlBindings', inputParamId))
    dispatch(onChangeInputParamsUserSelection(sqlValues, 'sqlValues', inputParamId))
    dispatch(onChangeInputParamsReducer({ columns: [], data: [], filteredData: [], isError: false }, 'fileData', inputParamId))
    return dispatch(executeSingleInputParam({ inputParamId, ...calls }))
  }

  // This method parse user configuration for input params from local storage and attach it to input params config, to create a default user selection object
  const configToUserInputParams = (inputParamsConfig, omitDefaults = false) => (dispatch, getState) => {
    if(!inputParamsConfig) {
      return;
    }
    const definedTime = getState().myAccountState.settingsState.definedTime
    const settings = getState().myAccountState.settingsState
    const inputParamsUserSelection = {}
    Object.entries(inputParamsConfig || {}).forEach(([id, {
      from, 
      to,
      keys = [],
      fromSelected,
      toSelected,
      values = [],
      curveKeys = [],
      lastValue
    }]) => {
      const savedSettingsRaw = omitDefaults ? "{}" : settings[`inputParams_${id}`],
            savedSettings = CommonService.parseJson(savedSettingsRaw, {}),
            symbols = getBasicKeys(keys, savedSettings),
            curveKeysParsed = getBasicCurveKeys(curveKeys, savedSettings),
            filters = values.map((valueObj, valueIndex) => ({ operator: valueObj.operator, name: valueObj.column, value: valueObj.defaultValue, index: valueIndex }));
      (savedSettings?.filters || []).forEach(f => {
        const filter = filters.find(oldFilter => oldFilter.name === f.name)
        if(filter) {
          filter.value = f.value;
        }
      });
      inputParamsUserSelection[id] = {
        fromDate: getDate({ date: savedSettings?.from || from, dateSelected: fromSelected, definedTime }),
        toDate: getDate({ date: savedSettings?.to || to, dateSelected: toSelected, definedTime }),
        symbols: ReduxService.updateObject(symbols, curveKeysParsed),
        filters,
        lastValue: savedSettings?.lastValue || lastValue,
        sqlValues: savedSettings?.sqlValues || {},
      }
    })
    dispatch(basicCustomInsightActions.onChange(inputParamsUserSelection, 'inputParamsUserSelection'))
  }

  const getDate = ({ date, dateSelected, definedTime }) => {
    if((!ReduxService.nonNullOrUndefined(date) || date === "") && dateSelected === DataStorageEnums.DATE_BETWEEN_TYPE.TODAY) {
      return TimeService.getCurrentDate(definedTime)
    } else {
      return date;
    }
  }

  const getBasicKeys = (keys = [], savedSettings = {}) => {
    const basic = {}
    keys.forEach(({ symbolKey, defaultValue }) => {
      basic[symbolKey] = hasOwn({ object: savedSettings.symbols, key: symbolKey }) ? savedSettings.symbols[symbolKey] : defaultValue
    })
    return basic;
  }

  const getBasicCurveKeys = (curveKeys = [], savedSettings = {}) => {
    const basic = {}
    curveKeys.forEach(({ symbolKey, defaultValue }) => {
      basic[symbolKey] = hasOwn({ object: savedSettings.symbols, key: symbolKey }) ? savedSettings.symbols[symbolKey] : defaultValue
    })
    return basic;
  }

  const getBasicColumns = ({ fileColumns = [], savedSettings = {} }) => {
    const basic = {}
    fileColumns.forEach(({ column, defaultValue, defaultValueFrom, defaultValueTo, ...props }) => {
      const savedColumn = savedSettings?.fileColumns?.[column] || {}
      basic[column] = {
        ...props,
        value: hasOwn({ object: savedColumn, key: 'value' }) ? savedColumn.value : defaultValue,
        valueFrom: hasOwn({ object: savedColumn, key: 'valueFrom' }) ? savedColumn.valueFrom : defaultValueFrom,
        valueTo: hasOwn({ object: savedColumn, key: 'valueTo' }) ? savedColumn.valueTo : defaultValueTo,
      }
    })
    return basic;
  }

  const getFilterName = filter => filter?.name || filter?.column || filter?.label

  const getFiltersParsed = ({ filters = [], savedFilters = [], userFilters = [] }) => 
    filters
      .map((filter, index) => {
        const name = getFilterName(filter),
              savedFilter = savedFilters.find(saved => saved?.index === index || getFilterName(saved) === name),
              userFilter = userFilters.find(user => user?.index === index || getFilterName(user) === name)

        return {
          name,
          value: userFilter?.value ?? savedFilter?.value ?? filter.value ?? filter.defaultValue,
          operator: filter.operator,
          type: filter.type
        }
      })
      .filter(filter => filter.value !== "" && filter.value !== undefined)

  const getInputParamFile = ({ 
    file, 
    onComplete, 
    onError, 
    onHandleRequest, 
    templateContent, 
    searchFilesCall,
    getFileDetailsCall,
    inputParamsTitle
  }) => (dispatch, getState) => {
    onHandleRequest()
    if(templateContent) {
      const templateParams = getState().insightState.customState.templateParams || {}
      onComplete({ data: templateParams[templateContent]?.data, columns: templateParams[templateContent]?.columns })
      onHandleRequest({ isResponse: true })
      return;
    }
    // this optimization cause a refresh (on tile based on input params based on file) to stop working
    // keeping it here just in case
    // const { inputParams = {}, fileData = {} } = getState().insightState.customState.inputParams[inputParamId] || {}
    // if(inputParams.csvFile?.name === file?.name && inputParams.csvFile?.groupName === file?.groupName && fileData?.data) {
    //   onComplete({ data: fileData?.data, columns: fileData?.columns })
    //   onHandleRequest({ isResponse: true })
    //   return;
    // }
    let idPromise
    if(!file) {
      onHandleRequest({ isResponse: true })
      return
    }
    if(file?.id) {
      idPromise = Promise.resolve().then(() => file.id)
    } else {
      const query = `groupName=${file?.groupName}&name=${file?.name}` 
      idPromise = searchFilesCall({ query, size: 1 })
        .then(({ items } = {}) => items?.[0]?.fid)
    }
    const getMetadataRequest = id =>
      getFileDetailsCall({ id })
        .then(response => response.headers.get("Location"))

    const getFileCall = location => {
      parse(location, {
        download: true,
        skipEmptyLines: true,
        header: true,
        worker: true,
        complete: result => {
          const newResult = result?.data || []
          if(newResult[newResult?.length - 1]?.length !== newResult[0]?.length) {
            newResult.pop()
          }
          if(result?.errors.length !== 0) {
            if(result?.data?.length === 0){
              dispatch(globalActions.getMessageStore()).error("No data has been received.")
            }
            onHandleRequest({ isResponse: true })
            return onError ? onError({ error: result?.errors, onError, onHandleRequest }) : Promise.resolve()
          } else {
            onComplete({ data: newResult, columns: result?.meta?.fields || [] })
            onHandleRequest({ isResponse: true })
          }
        },
        error: error => {
          onError?.()
          dispatch(statusBoxActions.addItem({ message: "CSV file can not be parsed.", title: inputParamsTitle, variant: StatusesEnums.STATUS.ERROR }))
          console.error(error)
          onHandleRequest({ isResponse: true })
          return Promise.resolve()
        },
        preview: basicCustomInsightConsts.ABSOLUTE_SIZE
      })
    }
    return idPromise.then(getMetadataRequest).then(getFileCall)
  }

  const getInputParamsConfigWithUserInput = ({ sourceInputParamsId, overwrittenInputParameters = {} }) => (dispatch, getState) => {
    const ipConfig = getState().insightState.customState.content?.inputParams?.[sourceInputParamsId] || {},
          ipUserSelection = getState().insightState.customState.inputParamsUserSelection?.[sourceInputParamsId] || {},
          definedTime = getState().myAccountState.settingsState.definedTime,
          settings = getState().myAccountState.settingsState,
          savedSettingsRaw = settings[`inputParams_${sourceInputParamsId}`],
          savedSettings = CommonService.parseJson(savedSettingsRaw, {})

    if(ipConfig.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.TS.value) {
      // get from config with user defaults attached
      const symbols = getBasicKeys(ipConfig.keys, savedSettings)
      // apply user selection
      Object.entries(ipUserSelection.symbols || {}).forEach(([symbolKey, symbolValues]) => {
        symbols[symbolKey] = symbolValues
      })
      const filters = getFiltersParsed({ filters: ipConfig.values, savedFilters: savedSettings.filters, userFilters: ipUserSelection.filters }),
            from = ipConfig?.withDateRange ? (ipUserSelection.fromDate === "" ? "" : getDate({ date: ipUserSelection.fromDate || savedSettings.from || ipConfig.from, dateSelected: ipConfig.fromSelected, definedTime })) : undefined,
            to = ipConfig?.withDateRange ? (ipUserSelection.toDate === "" ? "" : getDate({ date: ipUserSelection.toDate || savedSettings.to || ipConfig.to, dateSelected: ipConfig.toSelected, definedTime })): undefined,
            specificDate = ipConfig?.withDateRange ? getDate({ date: ipUserSelection.specificDate ?? savedSettings.specificDate ?? ipConfig.specificDate, dateSelected: undefined, definedTime }): undefined,
            lastValue = ipConfig?.withDateRange ? (ipUserSelection.lastValue === "" ? ipUserSelection.lastValue : (ipUserSelection.lastValue || savedSettings.lastValue || ipConfig.lastValue)) : undefined,
            lastType = ipConfig.lastType || DataStorageEnums.LAST_TYPE.VALUE,
            selectedTsColumns = ipUserSelection.selectedTsColumns || savedSettings.selectedTsColumns || ipConfig.columns

      // apply overwritten parameters
      const symbolsFinal = {}, metadatasFinal = {}
      ipConfig?.keys?.forEach(key => {
        if(key.symbolType === DataStorageEnums.KEY_TYPE.METADATA) {
          metadatasFinal[key.symbolKey] = getOverwrittenInputParamValue({ fallbackValue: symbols[key.symbolKey], includeMetadata: true, key: key.symbolKey, overwrittenInputParameters })
        } else {
          symbolsFinal[key.symbolKey] = getOverwrittenInputParamValue({ fallbackValue: symbols[key.symbolKey], key: key.symbolKey, overwrittenInputParameters })
        }
      })
      return {
        ...ipConfig,
        filters,
        from,
        to,
        specificDate,
        lastValue,
        lastType,
        columns: selectedTsColumns,
        symbols: symbolsFinal,
        metadatas: metadatasFinal
      }
    } else if(ipConfig.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.CURVE.value) {
      // get from config with user defaults attached
      const symbols = getBasicKeys(ipConfig.curveKeys, savedSettings),
            from = ipConfig?.withDateRange ? (ipUserSelection.fromDate === "" ? "" : getDate({ date: ipUserSelection.fromDate || savedSettings.from || ipConfig.from, dateSelected: ipConfig.fromSelected, definedTime })) : undefined,
            to = ipConfig?.withDateRange ? (ipUserSelection.toDate === "" ? "" : getDate({ date: ipUserSelection.toDate || savedSettings.to || ipConfig.to, dateSelected: ipConfig.toSelected, definedTime })) : undefined,
            specificDate = ipConfig?.withDateRange ? getDate({ date: ipUserSelection.specificDate ?? savedSettings.specificDate ?? ipConfig.specificDate, dateSelected: undefined, definedTime }) : undefined,
            lastValue = ipConfig?.withDateRange ? (ipUserSelection.lastValue === "" ? ipUserSelection.lastValue : (ipUserSelection.lastValue || savedSettings.lastValue || ipConfig.lastValue)) : undefined,
            lastType = ipConfig.lastType || DataStorageEnums.LAST_TYPE.VALUE,
            selectedTsColumns = ipUserSelection.selectedTsColumns || savedSettings.selectedTsColumns || (ipConfig.column ? [ipConfig.column] : undefined)
      // apply user selection
      Object.entries(ipUserSelection.symbols || {}).forEach(([symbolKey, symbolValues]) => {
        symbols[symbolKey] = symbolValues
      })
      // apply overwritten parameters
      const symbolsFinal = {}, metadatasFinal = {}
      ipConfig?.curveKeys?.forEach(key => {
        if(key.symbolType === DataStorageEnums.KEY_TYPE.METADATA) {
          metadatasFinal[key.symbolKey] = getOverwrittenInputParamValue({ fallbackValue: symbols[key.symbolKey], includeMetadata: true, key: key.symbolKey, overwrittenInputParameters })
        } else {
          symbolsFinal[key.symbolKey] = getOverwrittenInputParamValue({ fallbackValue: symbols[key.symbolKey], key: key.symbolKey, overwrittenInputParameters })
        }
      })
      return {
        ...ipConfig,
        from,
        to,
        specificDate,
        lastValue,
        lastType,
        columns: selectedTsColumns,
        symbols: symbolsFinal,
        metadatas: metadatasFinal
      }
    } else if(ipConfig.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.FILE.value) {
      // get from config with user defaults attached
      const basic = getBasicColumns({ fileColumns: ipConfig.fileColumns, savedSettings })
      const fileColumns = ReduxService.updateObject(basic, {});
      // apply user selection
      Object.entries(ipUserSelection.fileColumns || {}).forEach(([k, v]) => {
        fileColumns[k] = ReduxService.updateObject(fileColumns[k] || {}, v)
      })
      const fileColumnsFinal = {}
      ipConfig?.fileColumns?.forEach(column => {
        fileColumnsFinal[column.column] = overwrittenInputParameters.fileColumns?.[column.column] || fileColumns[column.column]
      })
      return {
        ...ipConfig,
        fileColumns: fileColumnsFinal
      }
    } else if(ipConfig.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.SQL.value) {
      const sqlValues = SqlSourceService.resolveBindingValues({
        bindings: ipConfig.sqlBindings || [],
        overrides: overwrittenInputParameters.sqlValues || {},
        currentSelection: ipUserSelection.sqlValues || {},
        personalDefaults: savedSettings.sqlValues || {}
      })
      return { ...ipConfig, sqlValues }
    }
  }

  const getTilesByType = (tiles, type) => tiles.filter(t => t.type === type)

  const getAllLabelsFromConfig = config => [
    ...(config?.keys || []).map(k => k.label),
    ...(config?.fileColumns || []).map(fc => fc.label),
    ...(config?.curveKeys || []).map(ck => ck.label),
    ...(config?.sqlBindings || []).map(binding => binding.name)
  ].filter(Boolean)

  const getMatchingSyncInputParamsIds = ({ inputParamConfigs, inputParamId }) => {
    const sourceLabels = getAllLabelsFromConfig(inputParamConfigs?.[inputParamId])

    return Object.entries(inputParamConfigs || {})
      .filter(([id, config]) => id !== inputParamId && config.shouldSynchronizeParams === true)
      .filter(([, config]) => getAllLabelsFromConfig(config).some(label => sourceLabels.includes(label)))
      .map(([id]) => id)
  }

  const executeSingleInputParam = ({ overwrittenInputParameters = {}, inputParamId, withMessage = false, searchFilesCall, getFileDetailsCall, getDataCall, getCurvesDataCall, getDataFromSqlCall }) => (dispatch, getState) => {
    const tiles = getState().insightState.customState.content.tiles,
          tilesWithMatchingSourceInputParamsId = tiles.filter(t => t.source?.value?.id === inputParamId),
          tableTiles = getTilesByType(tilesWithMatchingSourceInputParamsId, TILE_TYPES.TABLE),
          chartTiles = getTilesByType(tilesWithMatchingSourceInputParamsId, TILE_TYPES.CHART),
          statsTiles = getTilesByType(tilesWithMatchingSourceInputParamsId, TILE_TYPES.STATS),
          areThereAnyChartTiles = chartTiles.length > 0, areThereAnyTableTiles = tableTiles.length > 0, areThereAnyStatsTiles = statsTiles.length > 0,
          inputParamsTitle = overwrittenInputParameters.title,
          inputParams = dispatch(getInputParamsConfigWithUserInput({ sourceInputParamsId: inputParamId, overwrittenInputParameters } )) || {}

    if(inputParams.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.TS.value || !inputParams.basicInputParamsType) {
      dispatch(executeTimeSeriesInputParams({ areThereAnyChartTiles, areThereAnyStatsTiles, areThereAnyTableTiles, withMessage, inputParamId, inputParams, getDataCall, chartTiles, tableTiles, statsTiles, tiles }))
    } else if(inputParams.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.FILE.value) {
      dispatch(executeFileInputParams({ chartTiles, tableTiles, tiles, inputParamId, inputParams, withMessage, searchFilesCall, getFileDetailsCall, inputParamsTitle }))
    } else if(inputParams.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.CURVE.value) {
      dispatch(executeCurvesInputParams({ inputParams, chartTiles, tableTiles, tiles, inputParamId, areThereAnyChartTiles, areThereAnyTableTiles, withMessage, getCurvesDataCall }) )
    } else if(inputParams.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.SQL.value) {
      dispatch(executeSqlInputParams({ inputParamId, inputParams, overwrittenInputParameters, chartTiles, tableTiles, tiles, withMessage, getDataFromSqlCall }))
    }
  }

  const executeSyncInputParams = ({ inputParamId, ...rest }) => (dispatch, getState) => {
    const inputParamConfigs = getState().insightState.customState.content?.inputParams,
          inputParamsWithMatchingNames = getMatchingSyncInputParamsIds({ inputParamConfigs, inputParamId })
    inputParamsWithMatchingNames.forEach(id => dispatch(executeSingleInputParam({ inputParamId: id, ...rest })))
  }

  const executeAllInputParams = ({ ...rest } = {}) => (dispatch, getState) => {
    const configs = getState().insightState.customState.content?.inputParams || {}
    Object.keys(configs).forEach(id => {
      dispatch(executeSingleInputParam({ inputParamId: id, ...rest }))
    })
  }

  const FILE_INPUT_PARAMS_VALUE_TYPES = {
    DATE: {
      label: "Date",
      value: "DATE"
    },
    TEXT: {
      label: "Text",
      value: undefined
    },
  }

  return {
    getDate,
    configToUserInputParams,
    ON_CHANGE_INPUT_PARAMS,
    executeSingleInputParam,
    onChangeInputParamsConfig,
    ON_CHANGE_INPUT_PARAMS_CONFIG,
    confirmSqlInputParams,
    ON_CHANGE_INPUT_PARAMS_LIST,
    IMPORT_INPUT_PARAMS_REPLACE_TILE,
    importInputParamsReplaceTile,
    onChangeInputParamsList,
    getAvailablePresetLabel,
    getNewPresetLabel,
    ON_CHANGE_INPUT_PARAMS_USER_SELECTION,
    onChangeInputParamsUserSelection,
    updateMatchingDatePreset,
    selectDatePreset,
    selectScopePreset,
    ON_ADD_INPUT_PARAMS_LIST,
    ON_REMOVE_INPUT_PARAMS_LIST,
    onAddInputParamsList,
    onRemoveInputParamsList,
    addInputParams,
    ADD_INPUT_PARAMS,
    deleteInputParam,
    DELETE_INPUT_PARAM,
    executeAllInputParams,
    BASIC_INPUT_PARAM_TYPES,
    onReorderInputParamsList,
    ON_REORDER_INPUT_PARAMS_LIST,
    FILE_INPUT_PARAMS_VALUE_TYPES,
    changeOtherInputParams,
    executeSyncInputParams,
    onChangeInputParamsReducer,
    getRequestIds,
    duplicateInputParams,
    DUPLICATE_INPUT_PARAMS,
    DEFAULT_CURVE_KEYS,
    getCurveData,
    fetchCurveTableData,
    parseResultForTable,
    parseResultsForChart,
    getCurveBody,
    getCurveBodyKeys,
    getCurveItemsFromInputParams,
    getCurveItemsWithSources,
    getGroupSources,
    getInputParamsConfigWithUserInput,
    getMatchingSyncInputParamsIds
  }
})()
